import { useState } from "react";
import { toast } from "sonner";
import { Crown, Shield, UserPlus, Pencil, X, Ban, CheckCircle2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";

const Select = ({ label, value, onChange, children }) => (
  <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <select value={value} onChange={onChange} className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm">{children}</select>
  </label>
);

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");

export default function AdminUsers() {
  const { data: users, loading, reload } = useCrud(endpoints.adminUsers);
  const [invite, setInvite] = useState({ phone: "", displayName: "", role: "user" });
  const [showInvite, setShowInvite] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const sendInvite = async () => {
    if (!invite.phone) { toast.error("Phone required"); return; }
    try { await endpoints.adminInvite(invite); toast.success("Customer invited"); setInvite({ phone: "", displayName: "", role: "user" }); setShowInvite(false); reload(); }
    catch (e) { handleErr(e); }
  };

  const openEdit = (u) => setEdit({
    id: u.id, displayName: u.displayName || "", role: u.role || "user",
    tier: u.tier || "free", active: u.active !== false,
    subscriptionExpiresAt: toDateInput(u.subscriptionExpiresAt), phone: u.phone, email: u.email,
  });

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = {
        displayName: edit.displayName || null, role: edit.role, tier: edit.tier,
        active: edit.active,
        subscriptionExpiresAt: edit.subscriptionExpiresAt ? new Date(edit.subscriptionExpiresAt + "T23:59:59Z").toISOString() : null,
      };
      await endpoints.adminUpdateUser(edit.id, body);
      toast.success("Customer updated"); setEdit(null); reload();
    } catch (e) { handleErr(e); } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.displayName || u.phone || u.email}? This cannot be undone.`)) return;
    try { await endpoints.adminDeleteUser(u.id); toast.success("Customer deleted"); reload(); }
    catch (e) { handleErr(e); }
  };

  const grant = async () => {
    const days = window.prompt("Grant active subscription for how many days? (technical-issue override)", "30");
    if (days === null) return;
    setSaving(true);
    try {
      const { data } = await endpoints.adminGrantSubscription(edit.id, { days: parseInt(days || "30", 10), tier: edit.tier === "free" ? "premium" : edit.tier, reason: "Manual access (technical payment issue)" });
      toast.success(`Access granted until ${new Date(data.expiresAt).toLocaleDateString()}`);
      setEdit(null); reload();
    } catch (e) { handleErr(e); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Customers" desc={`${users.length} account(s)`}>
        <Btn onClick={() => setShowInvite((s) => !s)} data-testid="admin-invite-toggle"><UserPlus className="w-4 h-4" /> Invite</Btn>
      </PageHeader>

      {showInvite && (
        <Card className="p-5 mb-6 grid sm:grid-cols-4 gap-3 items-end">
          <Field label="Phone" placeholder="+250788…" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} data-testid="invite-phone-input" />
          <Field label="Name" placeholder="Optional" value={invite.displayName} onChange={(e) => setInvite({ ...invite, displayName: e.target.value })} />
          <Select label="Role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
            <option value="user">User</option><option value="admin">Admin</option>
          </Select>
          <Btn onClick={sendInvite} data-testid="invite-submit-btn">Send invite</Btn>
        </Card>
      )}

      {loading ? <Loading /> : users.length === 0 ? <Empty /> : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-[#273042]">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold flex items-center gap-2 truncate">
                    {u.displayName || u.phone || u.email}
                    {u.tier !== "free" && <Crown className="w-3.5 h-3.5 text-[#2563EB]" />}
                    {u.active === false && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-400"><Ban className="w-3 h-3" /> suspended</span>}
                  </div>
                  <div className="text-xs text-slate-400">
                    {u.phone || u.email} · {u.tier}
                    {u.subscriptionExpiresAt && ` · until ${new Date(u.subscriptionExpiresAt).toLocaleDateString()}`}
                    {` · joined ${u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}`}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 ${u.role === "admin" ? "bg-[#E11D2A]/15 text-[#E11D2A]" : "bg-white/5 text-slate-300 border border-[#273042]"}`}>
                    <Shield className="w-3.5 h-3.5" /> {u.role}
                  </span>
                  <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} title="Edit"
                    className="w-8 h-8 rounded-lg bg-white/5 border border-[#273042] text-slate-300 hover:text-white flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                  <DeleteBtn onClick={() => del(u)} testid={`user-delete-${u.id}`} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEdit(null)}>
          <Card className="w-full max-w-md p-6" >
            <div onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-extrabold text-white">Edit customer</h2>
                <button onClick={() => setEdit(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div className="text-xs text-slate-400">{edit.phone || edit.email}</div>
                <Field label="Display name" value={edit.displayName} onChange={(e) => setEdit({ ...edit, displayName: e.target.value })} data-testid="edit-user-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Select label="Role" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                    <option value="user">User</option><option value="admin">Admin</option>
                  </Select>
                  <Select label="Tier" value={edit.tier} onChange={(e) => setEdit({ ...edit, tier: e.target.value })}>
                    <option value="free">Free</option><option value="premium">Premium</option>
                  </Select>
                </div>
                <Field label="Subscription expires" type="date" value={edit.subscriptionExpiresAt} onChange={(e) => setEdit({ ...edit, subscriptionExpiresAt: e.target.value })} data-testid="edit-user-expiry" />
                <button onClick={grant} disabled={saving} data-testid="grant-subscription-btn"
                  className="w-full h-11 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm border bg-[#2563EB]/10 border-[#2563EB]/30 text-[#93B4FF] hover:bg-[#2563EB]/20 disabled:opacity-60">
                  <Crown className="w-4 h-4" /> Grant active access (payment-issue override)
                </button>
                <button onClick={() => setEdit({ ...edit, active: !edit.active })} data-testid="edit-user-active"
                  className={`w-full h-11 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm border ${edit.active ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-amber-500/10 border-amber-500/30 text-amber-400"}`}>
                  {edit.active ? <><CheckCircle2 className="w-4 h-4" /> Active</> : <><Ban className="w-4 h-4" /> Suspended</>}
                </button>
                <div className="flex gap-3 pt-1">
                  <Btn variant="ghost" onClick={() => setEdit(null)} className="flex-1">Cancel</Btn>
                  <Btn onClick={saveEdit} disabled={saving} data-testid="edit-user-save" className="flex-1">{saving ? "Saving…" : "Save changes"}</Btn>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
